import { useRoofingEstimator, useCart, useSupplier } from "../../../../hooks/query";
import { useEffect, useState } from "react";
import { useProject } from "../../../../hooks";

export default function useMaterials(measurements: any) {
    const { project } = useProject();
    const { useIdentifyProducts } = useRoofingEstimator();
    const { useListByProjectId } = useCart();
    const { useListInventoryMaterials } = useSupplier();
    const identifiedProductsSvc = useIdentifyProducts();
    const cartListSvc = useListByProjectId();
    const listInventoryMaterialsSvc = useListInventoryMaterials();

    // dialog related states
    const [openAdditionalProductDialog, setOpenAdditionalProductDialog] = useState(false);

    const [identifiedProducts, setIdentifiedProducts] = useState<any>(null);
    const [missingProducts, setMissingProducts] = useState<any>([]);
    const [accordionGroupStatus, setAccordionGroupStatus] = useState<any>([]);
    const [materialSchema, setMaterialSchema] = useState<any[]>([]);
    const [cart, setCart] = useState<any[]>([]);
    const [priceRanges, setPriceRanges] = useState<Record<string, { min: number; max: number }>>({});
    const [isLoading, setIsLoading] = useState<boolean>(true);

    const getCartItems = async () => {
        const cartItems = await cartListSvc.mutateAsync({ ProjectId: project?.id });
        setCart(cartItems);
        return cartItems;
    };

    // Listen for supplier confirmation to reload cart items
    useEffect(() => {
        const handleSupplierConfirmed = () => {
            // Small delay to ensure cart updates are complete
            setTimeout(() => {
                getCartItems();
            }, 300);
        };

        window.addEventListener("cartItemsUpdated", handleSupplierConfirmed);
        return () => {
            window.removeEventListener("cartItemsUpdated", handleSupplierConfirmed);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [project?.id]);

    const getRequiredMaterials = async () => {
        try {
            const params = Object.fromEntries(
                measurements.map(({ MeasurementType, ActualValue, CalculatedValue }: any) => [
                    MeasurementType,
                    ActualValue ?? CalculatedValue,
                ])
            );

            const response = await identifiedProductsSvc.mutateAsync(params);
            setIdentifiedProducts(response);
            return response;
        } catch (error) {
            console.error("Error in getRequiredMaterials:", error);
        }
    };

    const getAllSupplierInventory = async () => {
        return suppliers;
    };

    const getProductDependencySummary = async () => {
        return dependencies;
    };

    const anyProductsAreMissingFromIdentifiedList = async (requiredMaterials: any) => {
        // might need to identify this from database operation, this data comes from
        // comparing supplier inventory against the identified products

        setMissingProducts(["Bath Vent"]);
    };

    const getAvailableProductWithDependencyAdded = async (
        requiredMaterials: any,
        allAvailableMaterials: any,
        dependencyDetails: any
    ) => {
        // add product meta details to the required products and create a new array
        // allAvailableMaterials now comes from backend (identify-products)
        // Normalize shape so each entry has consistent `name` and `Name` properties
        const allAvailableWithMeta = allAvailableMaterials.map((item: any) => ({
            ...item,
            name: item.ProductName ?? item.Name,
            Name: item.ProductName ?? item.Name,
        }));

        const requiredMaterialsWithMeta = requiredMaterials
            .map(({ ProductName }: any) => {
                const matched = allAvailableWithMeta.find((item: any) => item.name === ProductName);
                return matched || null;
            })
            .filter(Boolean); // Remove nulls
        /// const materialsWithDependencyTreeAdded = requiredMaterialsWithMeta
        const productSelectionMetaData = requiredMaterialsWithMeta.reduce((acc: any, curr: any) => {
            const name = curr.name;

            // Ensure current material is added
            if (!acc[name]) {
                acc[name] = { ...curr };
            }

            // Check for dependencies for this material
            const dependency = dependencyDetails.find((depProd: any) => depProd.Category === name);
            if (!dependency) {
                return acc;
            }

            for (const requiredCategory of dependency.RequiresCategories) {
                const existingDependency = acc[requiredCategory];

                if (existingDependency) {
                    // Add to existing dependency's dependents
                    acc[requiredCategory] = {
                        ...existingDependency,
                        DependentsOn: [...(existingDependency.DependentsOn || []), name],
                    };
                } else {
                    // Try to find metadata for the missing dependency
                    const productTypeMeta = allAvailableWithMeta.find((e: any) => e.name === requiredCategory);
                    if (productTypeMeta) {
                        acc[requiredCategory] = {
                            ...productTypeMeta,
                            name: requiredCategory,
                            Name: requiredCategory,
                            DependentsOn: [name],
                        };
                    }
                }
            }

            return acc;
        }, {});

        const finalMaterialSchema = Object.values(productSelectionMetaData);
        setMaterialSchema(finalMaterialSchema);
        return finalMaterialSchema;
    };

    const groupMaterialsBasedOnDependency = (materialSchema: any) => {
        const materialsForGrouping = materialSchema.reduce((acc: any, curr: any) => {
            const isDependencyFound = curr?.DependentsOn?.length > 0;
            // assumption -> only one dependency per item
            if (isDependencyFound) {
                if (acc[curr?.DependentsOn[0]]) {
                    acc[curr?.DependentsOn[0]].materials.push(curr.Name);
                    return acc;
                }
                acc[curr?.DependentsOn[0]] = {
                    materials: [curr.Name],
                    expanded: true,
                };
            }
            return acc;
        }, {});
        setAccordionGroupStatus(materialsForGrouping);
        return materialsForGrouping;
    };
    const initDataLoad = async () => {
        if (!project?.id) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        try {
            const requiredMaterials = await getRequiredMaterials();
            if (!requiredMaterials || !Array.isArray(requiredMaterials)) {
                setIsLoading(false);
                return;
            }

            // With the new backend identify-products, requiredMaterials already contains
            // ProductName, MeasurementType and Subcategories, so we can treat it as our
            // master list of available products instead of the old hardcoded productsFromManufacturer.
            const allAvailableProducts = requiredMaterials;
            const dependencyDetails = await getProductDependencySummary();
            const supplierDetails = await getAllSupplierInventory();
            await getCartItems();

            const missingProducts = await anyProductsAreMissingFromIdentifiedList(requiredMaterials);
            const finalMaterialSchema = await getAvailableProductWithDependencyAdded(
                requiredMaterials,
                allAvailableProducts,
                dependencyDetails
            );
            groupMaterialsBasedOnDependency(finalMaterialSchema);
        } catch (error) {
            console.error("Error loading materials:", error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        initDataLoad();
    }, [measurements]);

    // Calculate price ranges from supplier data
    useEffect(() => {
        const calculatePriceRanges = async () => {
            if (materialSchema.length === 0) return;

            try {
                // Fetch supplier inventory from backend (uses static data)
                const supplierDetails = await listInventoryMaterialsSvc.mutateAsync();
                console.log("Supplier details from API:", supplierDetails);

                // Calculate min and max prices for each material product from supplier data
                const ranges: Record<string, { min: number; max: number }> = {};

                // Get all unique product names from materialSchema and their subcategories
                materialSchema.forEach((product) => {
                    // Check prices for each subcategory
                    product.Subcategories?.forEach((subcategory: any) => {
                        const productName = subcategory.Name;
                        const prices: number[] = [];

                        // Collect prices from all suppliers for this product
                        supplierDetails.forEach((supplier: any) => {
                            const supplierProduct = supplier.Products?.find((p: any) => p.ProductName === productName);
                            if (supplierProduct && supplierProduct.UnitPrice) {
                                const price = Number(supplierProduct.UnitPrice);
                                if (!isNaN(price) && price > 0) {
                                    prices.push(price);
                                }
                            }
                        });

                        if (prices.length > 0) {
                            ranges[productName] = {
                                min: Math.min(...prices),
                                max: Math.max(...prices),
                            };
                        }
                    });
                });

                setPriceRanges(ranges);
            } catch (error) {
                console.error("Error calculating price ranges:", error);
            }
        };

        calculatePriceRanges();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [materialSchema]);

    return {
        cart,
        identifiedProducts,
        missingProducts,
        accordionGroupStatus,
        materialSchema,
        openAdditionalProductDialog,
        dependencies,
        priceRanges,
        isLoading,
        setOpenAdditionalProductDialog,
        setAccordionGroupStatus,
    };
}

const dependencies = [
    {
        Category: "Field Shingles",
        RequiresCategories: [
            "Hip & Ridge",
            "Starter",
            "Nails (15 SQ/box)",
            "Nails (750 LF/box)",
            "Nails (12 SQ/box)",
            "Field Dry In",
            "Sealant / Adhesive",
            "Spray Paint",
        ],
        Mappings: [
            {
                Product: "Tamko Heritage",
                RequiredProducts: [
                    { Category: "Hip & Ridge", Product: "Tamko H&R" },
                    { Category: "Starter", Product: "Tamko Starter" },
                    { Category: "Nails (15 SQ/box)", Product: "1 and quarter inch" },
                    { Category: "Nails (750 LF/box)", Product: "1 and half inch" },
                    { Category: "Nails (12 SQ/box)", Product: "Plastic Cap Nails" },
                    { Category: "Field Dry In", Product: "Synthetic" },
                    { Category: "Sealant / Adhesive", Product: "Tamko Sealant" },
                    { Category: "Spray Paint", Product: "Tamko Roofing Spray Paint" },
                ],
            },
            {
                Product: "OC Oakridge",
                RequiredProducts: [
                    { Category: "Hip & Ridge", Product: "OC H&R" },
                    { Category: "Starter", Product: "OC Starter" },
                    { Category: "Nails (15 SQ/box)", Product: "1 and quarter inch" },
                    { Category: "Nails (750 LF/box)", Product: "1 and half inch" },
                    { Category: "Nails (12 SQ/box)", Product: "Plastic Cap Nails" },
                    { Category: "Field Dry In", Product: "Synthetic" },
                ],
            },
            {
                Product: "GAF Armorshield II",
                RequiredProducts: [
                    { Category: "Hip & Ridge", Product: "Seal a Ridge IR" },
                    { Category: "Starter", Product: "GAF Starter" },
                    { Category: "Nails (15 SQ/box)", Product: "1 and quarter inch" },
                    { Category: "Nails (750 LF/box)", Product: "1 and half inch" },
                    { Category: "Nails (12 SQ/box)", Product: "Plastic Cap Nails" },
                    { Category: "Field Dry In", Product: "Synthetic" },
                ],
            },
            {
                Product: "OC Duration",
                RequiredProducts: [
                    { Category: "Hip & Ridge", Product: "OC H&R" },
                    { Category: "Starter", Product: "OC Starter" },
                    { Category: "Nails (15 SQ/box)", Product: "1 and quarter inch" },
                    { Category: "Nails (750 LF/box)", Product: "1 and half inch" },
                    { Category: "Nails (12 SQ/box)", Product: "Plastic Cap Nails" },
                    { Category: "Field Dry In", Product: "Synthetic" },
                ],
            },
            {
                Product: "OC Flex",
                RequiredProducts: [
                    { Category: "Hip & Ridge", Product: "OC H&R" },
                    { Category: "Starter", Product: "OC Starter" },
                    { Category: "Nails (15 SQ/box)", Product: "1 and quarter inch" },
                    { Category: "Nails (750 LF/box)", Product: "1 and half inch" },
                    { Category: "Nails (12 SQ/box)", Product: "Plastic Cap Nails" },
                    { Category: "Field Dry In", Product: "Synthetic" },
                ],
            },
            {
                Product: "Tamko IR",
                RequiredProducts: [
                    { Category: "Hip & Ridge", Product: "Tamko H&R" },
                    { Category: "Starter", Product: "Tamko Starter" },
                    { Category: "Nails (15 SQ/box)", Product: "1 and quarter inch" },
                    { Category: "Nails (750 LF/box)", Product: "1 and half inch" },
                    { Category: "Nails (12 SQ/box)", Product: "Plastic Cap Nails" },
                    { Category: "Field Dry In", Product: "Synthetic" },
                ],
            },
            {
                Product: "Cert. Flex",
                RequiredProducts: [
                    { Category: "Hip & Ridge", Product: "Shadowridge IR" },
                    { Category: "Starter", Product: "Cert. Starter" },
                    { Category: "Nails (15 SQ/box)", Product: "1 and quarter inch" },
                    { Category: "Nails (750 LF/box)", Product: "1 and half inch" },
                    { Category: "Nails (12 SQ/box)", Product: "Plastic Cap Nails" },
                ],
            },
            {
                Product: "Cert. Northgate",
                RequiredProducts: [
                    { Category: "Hip & Ridge", Product: "Shadowridge IR" },
                    { Category: "Starter", Product: "Cert. Starter" },
                    { Category: "Nails (15 SQ/box)", Product: "1 and quarter inch" },
                    { Category: "Nails (750 LF/box)", Product: "1 and half inch" },
                    { Category: "Nails (12 SQ/box)", Product: "Plastic Cap Nails" },
                ],
            },
            {
                Product: "GAF Grand Sequoia",
                RequiredProducts: [
                    { Category: "Hip & Ridge", Product: "Seal a Ridge" },
                    { Category: "Starter", Product: "GAF Starter" },
                    { Category: "Double Starter", Product: "GAF Seq/Canyon Start" },
                    { Category: "Nails (15 SQ/box)", Product: "1 and half inch" },
                    { Category: "Nails (750 LF/box)", Product: "1 and three quarter inch" },
                    { Category: "Nails (12 SQ/box)", Product: "Plastic Cap Nails" },
                    { Category: "Field Dry In", Product: "Synthetic" },
                ],
            },
            {
                Product: "GAF Grand Sequoia IR",
                RequiredProducts: [
                    { Category: "Hip & Ridge", Product: "Seal a Ridge IR" },
                    { Category: "Starter", Product: "GAF Starter" },
                    { Category: "Double Starter", Product: "GAF Seq/Canyon Start" },
                    { Category: "Nails (15 SQ/box)", Product: "1 and half inch" },
                    { Category: "Nails (750 LF/box)", Product: "1 and three quarter inch" },
                    { Category: "Nails (12 SQ/box)", Product: "Plastic Cap Nails" },
                    { Category: "Field Dry In", Product: "Synthetic" },
                ],
            },
            {
                Product: "GAF Grand Canyon",
                RequiredProducts: [
                    { Category: "Hip & Ridge", Product: "Timbertex" },
                    { Category: "Starter", Product: "GAF Starter" },
                    { Category: "Double Starter", Product: "GAF Seq/Canyon Start" },
                    { Category: "Nails (15 SQ/box)", Product: "1 and half inch" },
                    { Category: "Nails (750 LF/box)", Product: "1 and three quarter inch" },
                    { Category: "Nails (12 SQ/box)", Product: "Plastic Cap Nails" },
                    { Category: "Field Dry In", Product: "Synthetic" },
                ],
            },
            {
                Product: "Cert. Prez Shake",
                RequiredProducts: [
                    { Category: "Hip & Ridge", Product: "Cedar Creast" },
                    { Category: "Starter", Product: "Cert. Starter" },
                    { Category: "Double Starter", Product: "Cert. Pres Starter" },
                    { Category: "Nails (15 SQ/box)", Product: "1 and half inch" },
                    { Category: "Nails (750 LF/box)", Product: "1 and three quarter inch" },
                    { Category: "Nails (12 SQ/box)", Product: "Plastic Cap Nails" },
                ],
            },
            {
                Product: "Cert. Prez IR",
                RequiredProducts: [
                    { Category: "Hip & Ridge", Product: "Cedar Crest IR" },
                    { Category: "Starter", Product: "Cert. Starter" },
                    { Category: "Double Starter", Product: "Cert. Pres IR Starter" },
                    { Category: "Nails (15 SQ/box)", Product: "1 and half inch" },
                    { Category: "Nails (750 LF/box)", Product: "1 and three quarter inch" },
                    { Category: "Nails (12 SQ/box)", Product: "Plastic Cap Nails" },
                ],
            },
            {
                Product: "Cert. TL",
                RequiredProducts: [
                    { Category: "Hip & Ridge", Product: "Mountain Ridge" },
                    { Category: "Starter", Product: "Cert. Starter" },
                    { Category: "Double Starter", Product: "Cert. Starter" },
                    { Category: "Nails (15 SQ/box)", Product: "1 and half inch" },
                    { Category: "Nails (750 LF/box)", Product: "1 and three quarter inch" },
                    { Category: "Nails (12 SQ/box)", Product: "Plastic Cap Nails" },
                ],
            },
        ],
    },
    {
        Category: "Ridge Vent",
        RequiresCategories: ["Hip & Ridge"],
        Mappings: [
            {
                Product: "Master Flow Ridge Vent",
                RequiredProducts: [],
            },
        ],
    },
    {
        Category: "Ice & Water Shield",
        RequiresCategories: ["Drip Edge / Eave", "Drip Edge / Rake"],
        Mappings: [
            {
                Product: "3 ft",
                RequiredProducts: [
                    { Category: "Drip Edge / Eave", Product: "Aluminum Drip Edge" },
                    { Category: "Drip Edge / Rake", Product: "Steel Drip Edge" },
                ],
            },
            {
                Product: "4.5 ft",
                RequiredProducts: [
                    { Category: "Drip Edge / Eave", Product: "Aluminum Drip Edge" },
                    { Category: "Drip Edge / Rake", Product: "Steel Drip Edge" },
                ],
            },
            {
                Product: "6 ft",
                RequiredProducts: [
                    { Category: "Drip Edge / Eave", Product: "Aluminum Drip Edge" },
                    { Category: "Drip Edge / Rake", Product: "Steel Drip Edge" },
                ],
            },
        ],
    },
    {
        Category: "Valley Liner",
        RequiresCategories: ["Ice & Water Shield"],
        Mappings: [
            {
                Product: "Tarco",
                RequiredProducts: [{ Category: "Ice & Water Shield", Product: "3 ft" }],
            },
        ],
    },
];

const suppliers = [
    {
        Name: "ABC Roofing Supply",
        Products: [
            { ProductName: "OC Oakridge", Type: "Field Shingles", UnitPrice: "1" },
            { ProductName: "OC Ridge", Type: "Hip & Ridge" },
            { ProductName: "OC Starter", Type: "Starter" },
            { ProductName: "Premium Ice Guard", Type: "Ice & Water Shield" },
            { ProductName: "Roofing Nails", Type: "Nails" },
            { ProductName: "Synthetic Underlayment", Type: "Field Dry In" },
            { ProductName: "Aluminum Drip Edge", Type: "Drip Edge / Eave" },
            { ProductName: "Steel Drip Edge", Type: "Drip Edge / Rake" },
        ],
    },
    {
        Name: "XYZ Roofing Supply",
        Products: [
            { ProductName: "GAF Armorshield II", Type: "Field Shingles" },
            { ProductName: "GAF Ridge", Type: "Hip & Ridge" },
            { ProductName: "GAF Starter", Type: "Starter" },
            { ProductName: "Shingle Over Ridge Vent", Type: "Ridge Vent" },
            { ProductName: "Roofing Nails", Type: "Nails" },
            { ProductName: "Metal Valley Liner", Type: "Valley Liner" },
            { ProductName: "Plastic Roof Vent", Type: "Roof Vents" },
        ],
    },
    {
        Name: "Roofers Warehouse",
        Products: [
            { ProductName: "Double Layer Starter", Type: "Double Starter" },
            { ProductName: "FastenPro Roofing Nails", Type: "Nails" },
            { ProductName: "WeatherShield Ice Guard", Type: "Ice & Water Shield" },
            { ProductName: "EdgeTech Drip Edge", Type: "Drip Edge / Eave" },
            { ProductName: "EdgeGuard Steel Drip Edge", Type: "Drip Edge / Rake" },
        ],
    },
];
